import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { approveRequest, cancelRequest, rejectRequest } from 'features/requests/requestsSlice'
import { setConfirmModal } from 'features/global/globalSlice'
import { ModalTypes } from 'types/global.interface'
import { IRequestRecord } from 'types/requests.interface'
import { NavBar } from 'components/NavBar/NavBar'
import { Button } from 'components/ui/Button/Button'
import { PageWrapper, Flex, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { Head1 } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { isApprover } from './requestsData'
import { RequestsBoard } from './Sections/RequestsBoard'
import { RequestDrawer } from './Sections/RequestDrawer'
import { NewRequestModal } from './Sections/NewRequestModal'
import { HeaderRight, SegmentBar } from './Requests.styled'

export const Requests = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { currentUser } = useSelector((state: RootState) => state.requests)
  const [segment, setSegment] = useState<'approvals' | 'mine'>('approvals')
  const [selected, setSelected] = useState<IRequestRecord | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  const showApprovals = isApprover(currentUser.role)
  const activeSegment = showApprovals ? segment : 'mine'

  const confirm = (word: string, run: () => void) => {
    setSelected(null)
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => run(),
        setParams: { type: ModalTypes.BeforeSabe },
        word,
      }),
    )
  }

  const handleApprove = (note: string) => {
    if (!selected) return
    const id = selected.id
    confirm('approve this request', () => dispatch(approveRequest({ id, note })))
  }

  const handleReject = (note: string) => {
    if (!selected) return
    const id = selected.id
    confirm('reject this request', () => dispatch(rejectRequest({ id, note })))
  }

  const handleQuickApprove = (req: IRequestRecord) => {
    confirm('approve this request', () => dispatch(approveRequest({ id: req.id, note: '' })))
  }

  const handleCancel = (req: IRequestRecord) => {
    confirm('cancel this request', () => dispatch(cancelRequest(req.id)))
  }

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <Flex between center mb='16' style={{ minHeight: '2.5rem' }}>
          <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>
            Requests
          </Head1>
          <HeaderRight>
            <Button variant='primary' onClick={() => setModalOpen(true)}>
              New Request
            </Button>
          </HeaderRight>
        </Flex>

        <SegmentBar>
          <TabsContainer>
            {showApprovals && (
              <Tabs active={activeSegment === 'approvals'} onClick={() => setSegment('approvals')}>
                Approvals
              </Tabs>
            )}
            <Tabs active={activeSegment === 'mine'} onClick={() => setSegment('mine')}>
              My Requests
            </Tabs>
          </TabsContainer>
        </SegmentBar>

        <RequestsBoard
          key={activeSegment}
          scope={activeSegment}
          onSelect={setSelected}
          onQuickApprove={handleQuickApprove}
          onCancel={handleCancel}
        />
      </PageWrapper>

      {selected && (
        <RequestDrawer
          req={selected}
          scope={activeSegment}
          onClose={() => setSelected(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onCancel={() => handleCancel(selected)}
        />
      )}
      {modalOpen && (
        <NewRequestModal
          closeModal={(submitted?: boolean) => {
            setModalOpen(false)
            if (submitted) setSegment('mine')
          }}
        />
      )}
    </>
  )
}
